import { useEffect, useRef, useCallback } from 'react'

interface Article {
  id: number
  title: string
  feed_title: string
  feed_subscription_id: number | null
  published_at: string
  read: boolean
  url: string
  content: string | null
  author: string | null
  image_url: string | null
  reading_time_minutes: number | null
}

interface ArticleListProps {
  articles: Article[]
  hasMore: boolean
  loading: boolean
  loadingMore: boolean
  onLoadMore: () => void
  onMarkRead?: (articleId: number) => void
  feedId?: number | null
  category?: string | null
  sortDirection?: 'desc' | 'asc'
  viewMode?: 'compact' | 'expanded'
}

function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

export default function ArticleList({ articles, hasMore, loading, loadingMore, onLoadMore, onMarkRead, feedId, category, sortDirection, viewMode = 'compact' }: ArticleListProps) {
  function articleHref(url: string): string {
    const params = new URLSearchParams()
    if (feedId) params.set('feed_id', String(feedId))
    if (category) params.set('category', category)
    if (sortDirection === 'asc') params.set('sort', 'asc')
    const qs = params.toString()
    return qs ? `${url}?${qs}` : url
  }
  const sentinelRef = useRef<HTMLDivElement>(null)
  const swipeStateRef = useRef<{ startX: number; startY: number; articleId: number; el: HTMLElement; swiping: boolean } | null>(null)
  const didSwipeRef = useRef(false)

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>, articleId: number) => {
    if (viewMode !== 'expanded') return
    const el = e.currentTarget
    el.setPointerCapture(e.pointerId)
    swipeStateRef.current = { startX: e.clientX, startY: e.clientY, articleId, el, swiping: false }
  }, [viewMode])

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const state = swipeStateRef.current
    if (!state) return
    const dx = e.clientX - state.startX
    const dy = e.clientY - state.startY

    // If vertical movement is dominant, cancel the swipe
    if (!state.swiping && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) {
      swipeStateRef.current = null
      state.el.style.transform = ''
      state.el.style.opacity = ''
      return
    }

    // Only track left swipes (negative dx)
    if (dx < -10) {
      state.swiping = true
      const clampedDx = Math.max(dx, -200)
      state.el.style.transform = `translateX(${clampedDx}px)`
      state.el.style.opacity = String(Math.max(0.3, 1 + clampedDx / 300))
    }
  }, [])

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const state = swipeStateRef.current
    if (!state) return
    swipeStateRef.current = null

    const dx = e.clientX - state.startX
    didSwipeRef.current = state.swiping
    if (state.swiping && dx < -80 && onMarkRead) {
      state.el.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out'
      state.el.style.transform = 'translateX(-100%)'
      state.el.style.opacity = '0'
      state.el.addEventListener('transitionend', () => {
        state.el.style.transition = ''
        state.el.style.transform = ''
        state.el.style.opacity = ''
        onMarkRead(state.articleId)
      }, { once: true })
    } else {
      state.el.style.transition = 'transform 0.2s ease-out, opacity 0.2s ease-out'
      state.el.style.transform = ''
      state.el.style.opacity = ''
      const cleanup = () => { state.el.style.transition = '' }
      state.el.addEventListener('transitionend', cleanup, { once: true })
    }
  }, [onMarkRead])

  useEffect(() => {
    if (!hasMore || loadingMore) return

    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore()
        }
      },
      { threshold: 0 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, loadingMore, onLoadMore])

  if (loading) {
    return (
      <p style={{ padding: 'var(--sp-8) var(--sp-6)', color: 'var(--c-text-muted)', fontSize: 'var(--text-sm)' }}>
        Loading...
      </p>
    )
  }

  if (articles.length === 0) {
    return (
      <p style={{ padding: 'var(--sp-8) var(--sp-6)', color: 'var(--c-text-muted)', fontSize: 'var(--text-sm)' }}>
        No articles yet. <a href="/feeds">Add a feed</a> to get started.
      </p>
    )
  }

  return (
    <>
      {articles.map((article) => (
        viewMode === 'expanded' ? (
          <div key={article.id} className="article-swipe-wrapper">
            <div className="swipe-read-indicator">Mark as read ✓</div>
            <div
              className={`article-row-expanded ${article.read ? 'read' : 'unread'}`}
              style={{ touchAction: 'pan-y', cursor: 'pointer' }}
              onPointerDown={(e) => handlePointerDown(e, article.id)}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onClick={(e) => {
                if (didSwipeRef.current) { didSwipeRef.current = false; return }
                const target = e.target as HTMLElement
                if (target.closest('a')) return
                window.location.href = articleHref(article.url)
              }}
            >
            <div className="article-expanded-content">
              <div className="article-expanded-header">
                <a href={articleHref(article.url)} className="article-title-link">{article.title}</a>
                <span className="pub-date">{article.published_at}</span>
              </div>
              <div className="article-expanded-meta">
                {article.feed_subscription_id ? (
                  <a href={`/feed_subscriptions/${article.feed_subscription_id}`} className="feed-name" onClick={(e) => e.stopPropagation()}>{article.feed_title}</a>
                ) : (
                  <span className="feed-name">{article.feed_title}</span>
                )}
                {article.author && <span className="article-author">by {article.author}</span>}
                {article.reading_time_minutes != null && (
                  <span className="reading-time-badge" aria-label={`${article.reading_time_minutes} minute read`}>
                    <svg className="reading-time-icon" viewBox="0 0 16 16" aria-hidden="true">
                      <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M8 4.5V8l2.25 1.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    {article.reading_time_minutes} min read
                  </span>
                )}
              </div>
              {article.content && (
                <p className="article-preview">{stripHtml(article.content).slice(0, 200)}{stripHtml(article.content).length > 200 ? '…' : ''}</p>
              )}
            </div>
            {article.image_url && (
              <img src={article.image_url} alt="" className="article-thumbnail" loading="lazy" />
            )}
            </div>
          </div>
        ) : (
          <div key={article.id} className={`article-row ${article.read ? 'read' : 'unread'}`}>
            <a href={articleHref(article.url)} className="article-title-link">{article.title}</a>
            {article.feed_subscription_id ? (
              <a href={`/feed_subscriptions/${article.feed_subscription_id}`} className="feed-name" onClick={(e) => e.stopPropagation()}>{article.feed_title}</a>
            ) : (
              <span className="feed-name">{article.feed_title}</span>
            )}
            <span className="pub-date">{article.published_at}</span>
            {article.reading_time_minutes != null && <span className="reading-time">{article.reading_time_minutes} min read</span>}
          </div>
        )
      ))}

      {hasMore && (
        <div ref={sentinelRef} data-testid="scroll-sentinel" style={{ padding: 'var(--sp-4) var(--sp-6)' }}>
          {loadingMore && (
            <p style={{ color: 'var(--c-text-muted)', fontSize: 'var(--text-sm)' }}>
              Loading more...
            </p>
          )}
        </div>
      )}
    </>
  )
}
